import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import Header from "./components/Header";
import ProblemInput from "./components/ProblemInput";
import ProvocationDisplay from "./components/ProvocationDisplay";
import FeaturedVisualization from "./components/FeaturedVisualization";
import ShareLinks from "./components/ShareLinks";
import About from "./components/About";
import IqEstimateDisplay from "./components/IqEstimateDisplay";
import useTheme from "./hooks/useTheme";
import "./App.css";

const Home = ({ theme }) => {
  const [provocations, setProvocations] = useState([]);
  const [authorIqEstimate, setAuthorIqEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [featuredProvocationText, setFeaturedProvocationText] = useState("");
  const [featuredVisualization, setFeaturedVisualization] = useState("");
  // State for the input field, lifted from ProblemInput
  const [problem, setProblem] = useState("");
  const location = useLocation();

  const handleSubmit = useCallback(async (problemToSubmit) => {
    // If no problem is provided, do nothing.
    if (!problemToSubmit || !problemToSubmit.trim()) return;

    setIsLoading(true);
    setError(null);
    // Ensure the input field is updated if submission comes from URL
    setProblem(problemToSubmit);

    try {
      const apiUrl =
        process.env.NODE_ENV === "production"
          ? "/api/provocations"
          : "http://localhost:5000/api/provocations";

      const response = await axios.post(apiUrl, { problem: problemToSubmit });

      if (response.data) {
        // Check if the response has the new structure with authorIqEstimate
        if (response.data.provocations && Array.isArray(response.data.provocations)) {
          setProvocations(response.data.provocations);
          setAuthorIqEstimate(response.data.authorIqEstimate || null);
        } else if (Array.isArray(response.data)) {
          // Handle legacy format (just an array of provocations)
          setProvocations(response.data);
          setAuthorIqEstimate(null);
        } else {
          console.error("Unexpected response format:", response.data);
          setError("Received an unexpected response from the server.");
          setProvocations([]);
          setAuthorIqEstimate(null);
        }
      } else {
        console.error("Empty response received");
        setError("Received an empty response from the server.");
        setProvocations([]);
        setAuthorIqEstimate(null);
      }
    } catch (err) {
      console.error("Error fetching provocations:", err);
      setError("Failed to generate provocations. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  }, []); // useCallback to prevent re-creation on each render

  const handleRemix = useCallback(async (provocationText) => {
    setFeaturedProvocationText(provocationText);
    setFeaturedVisualization("");

    try {
      const apiUrl =
        process.env.NODE_ENV === "production"
          ? "/api/provocations/visualize"
          : "http://localhost:5000/api/provocations/visualize";

      const response = await axios.post(apiUrl, { provocationText });

      if (response.data && response.data.visualization) {
        setFeaturedVisualization(response.data.visualization);
      }
    } catch (err) {
      console.error("Error fetching remixed visualization:", err);
    }
  }, []);

  // Effect to handle the 'problem' from the query string on page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const problemFromQuery = params.get('problem');
    if (problemFromQuery) {
      const decodedProblem = decodeURIComponent(problemFromQuery);
      // Automatically submit the problem from the URL
      handleSubmit(decodedProblem);
    }
  }, [location.search, handleSubmit]);

  return (
    <main className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <ProblemInput
          problem={problem}
          setProblem={setProblem}
          onSubmit={() => handleSubmit(problem)}
          isLoading={isLoading}
        />

        <ShareLinks problem={problem} />

        {error && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 dark:bg-red-900 dark:border-red-700 dark:text-red-300 p-4 mb-8"
            role="alert"
          >
            <p>{error}</p>
          </div>
        )}

        {provocations.length > 0 && (
          <>
            <IqEstimateDisplay iqEstimate={authorIqEstimate} question={problem} />

            <ProvocationDisplay
              provocations={provocations}
              onRemix={handleRemix}
              theme={theme}
            />

            <FeaturedVisualization
              provocationText={featuredProvocationText}
              visualizationCode={featuredVisualization}
              theme={theme}
            />
          </>
        )}
      </div>
    </main>
  );
};

function App() {
  const [theme, setTheme] = useTheme();

  return (
    <Router>
      <div className={`min-h-screen bg-gray-50 dark:bg-gray-900`}>
        <Header theme={theme} setTheme={setTheme} />
        <Routes>
          <Route path="/" element={<Home theme={theme} />} />
          <Route path="/about" element={<About />} />
        </Routes>
        <footer className="bg-gray-100 dark:bg-gray-800 py-6 mt-12">
          <div className="container mx-auto px-4 text-center text-gray-600 dark:text-gray-400">
            <p>
              © {new Date().getFullYear()} ProvocaBot - A Lateral Thinking Tool
              by&nbsp;
              <a
                href="https://www.iShipCode.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                Adrian Scott
              </a>
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
